import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDateTime, formatNumber } from '../../utils/formatters.js';

export function ContractDetailModal({ contractId, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [callOff, setCallOff] = useState({ quantity: '', reference: '' });
  const [error, setError] = useState(null);

  const detail = useQuery({ queryKey: ['inventory', 'contract', contractId], queryFn: () => inventoryApi.getContract(contractId).then((r) => r.items), enabled: Boolean(contractId) });
  const c = detail.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['inventory', 'contracts'] });
    queryClient.invalidateQueries({ queryKey: ['inventory', 'contract', contractId] });
  };
  const release = useMutation({
    mutationFn: () => inventoryApi.releaseContract(contractId, { quantity: Number(callOff.quantity), reference: callOff.reference || null }),
    onSuccess: () => { setError(null); setCallOff({ quantity: '', reference: '' }); invalidate(); },
    onError: (e) => setError(e?.message || t('common.loadError')),
  });
  const close = useMutation({ mutationFn: () => inventoryApi.closeContract(contractId), onSuccess: invalidate, onError: (e) => setError(e?.message) });

  const active = c?.status === 'ACTIVE';
  const pct = c ? Math.min(100, Math.round((Number(c.releasedQuantity) / Number(c.targetQuantity)) * 100)) : 0;

  return (
    <Modal
      open={Boolean(contractId)}
      onClose={onClose}
      title={c ? `${c.contractNumber} · ${c.productSku}` : t('ct.title')}
      footer={<>
        <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
        {active && <Button variant="ghost" disabled={close.isPending} onClick={() => close.mutate()}>{t('ct.action.close')}</Button>}
      </>}
    >
      {c && (
        <div className="form-stack">
          <p className="muted text-sm" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge variant={active ? 'success' : 'neutral'}>{t(`ct.status.${c.status}`)}</Badge>
            {c.supplierName} · {c.productName}
            {c.validFrom ? ` · ${c.validFrom}-${c.validTo || ''}` : ''}
          </p>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span>{t('ct.col.released')}: <strong className="mono">{formatNumber(c.releasedQuantity)}</strong> / {formatNumber(c.targetQuantity)}</span>
              <span>{t('ct.col.remaining')}: <strong className="mono">{formatNumber(c.remainingQuantity)}</strong></span>
            </div>
            <div style={{ height: 8, background: 'var(--color-border)', borderRadius: 4, overflow: 'hidden', marginTop: 4 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--color-accent, #1d4ed8)' }} />
            </div>
          </div>

          {active && (
            <div className="field-row" style={{ alignItems: 'flex-end' }}>
              <label className="field" style={{ maxWidth: 130 }}><span className="field-label">{t('ct.field.callOffQty')}</span>
                <input className="input num" type="number" min="0.0001" step="any" value={callOff.quantity} onChange={(e) => setCallOff((s) => ({ ...s, quantity: e.target.value }))} />
              </label>
              <label className="field"><span className="field-label">{t('ct.field.reference')}</span>
                <input className="input" placeholder="PO-…" value={callOff.reference} onChange={(e) => setCallOff((s) => ({ ...s, reference: e.target.value }))} />
              </label>
              <Button variant="primary" disabled={release.isPending || !callOff.quantity} onClick={() => release.mutate()}>{t('ct.action.release')}</Button>
            </div>
          )}

          <div className="settings-divider">{t('ct.releases')}</div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th className="num">{t('ct.field.callOffQty')}</th><th>{t('ct.field.reference')}</th><th>{t('ct.col.time')}</th></tr></thead>
              <tbody>
                {(c.releases || []).map((r) => (
                  <tr key={r.id}><td className="num mono">{formatNumber(r.quantity)}</td><td>{r.reference || '-'}</td><td className="muted text-sm">{formatDateTime(r.createdAt)}</td></tr>
                ))}
                {(c.releases || []).length === 0 && <tr><td colSpan={3} className="muted">{t('ct.noReleases')}</td></tr>}
              </tbody>
            </table>
          </div>
          {error && <div className="alert danger">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
